// ==========================================================
// Shared chrome for the static content pages
// (About / Contact / Privacy / Terms / 404).
// Mirrors the in-game theme & language preferences so the
// whole site feels like one product.
// The stylesheet is linked directly from each page, matching the
// convention used by index.html.
// ==========================================================

type PageTheme = 'light' | 'dark';
type PageLang = 'ja' | 'en';

const THEME_KEY = 'paint_meister_theme';
const LANG_KEY = 'paint_meister_lang';

function readStorage(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeStorage(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* Storage may be unavailable (private mode) - preference is simply not persisted. */
  }
}

function resolveTheme(): PageTheme {
  const saved = readStorage(THEME_KEY);
  return saved === 'light' || saved === 'dark' ? saved : 'dark';
}

function resolveLang(): PageLang {
  const saved = readStorage(LANG_KEY);
  if (saved === 'ja' || saved === 'en') return saved;
  return navigator.language.toLowerCase().startsWith('ja') ? 'ja' : 'en';
}

function applyTheme(theme: PageTheme): void {
  document.body.setAttribute('data-theme', theme);
  const icon = document.getElementById('pg-theme-icon');
  if (icon) icon.textContent = theme === 'dark' ? '☀️' : '🌙';
}

function applyLang(lang: PageLang): void {
  document.documentElement.setAttribute('lang', lang);
  document.querySelectorAll<HTMLElement>('[data-lang-block]').forEach(el => {
    el.classList.toggle('is-visible', el.dataset.langBlock === lang);
  });
  document.querySelectorAll<HTMLElement>('[data-i18n-ja][data-i18n-en]').forEach(el => {
    el.textContent = (lang === 'ja' ? el.dataset.i18nJa : el.dataset.i18nEn) ?? el.textContent;
  });
  document.querySelectorAll<HTMLElement>('[data-aria-ja][data-aria-en]').forEach(el => {
    const aria = lang === 'ja' ? el.dataset.ariaJa : el.dataset.ariaEn;
    if (aria) el.setAttribute('aria-label', aria);
  });
  const label = document.getElementById('pg-lang-label');
  if (label) label.textContent = lang === 'ja' ? '🇯🇵 JP' : '🇺🇸 EN';
}

function initPageChrome(): void {
  let theme = resolveTheme();
  let lang = resolveLang();

  applyTheme(theme);
  applyLang(lang);

  document.getElementById('pg-theme-toggle')?.addEventListener('click', () => {
    theme = theme === 'dark' ? 'light' : 'dark';
    writeStorage(THEME_KEY, theme);
    applyTheme(theme);
  });

  document.getElementById('pg-lang-toggle')?.addEventListener('click', () => {
    lang = lang === 'ja' ? 'en' : 'ja';
    writeStorage(LANG_KEY, lang);
    applyLang(lang);
  });

  const yearEl = document.getElementById('pg-year');
  if (yearEl) yearEl.textContent = String(new Date().getFullYear());
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initPageChrome);
} else {
  initPageChrome();
}
